import React, { useContext, useEffect, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView } from 'react-native'
import { Button, Text, Spinner, Toggle } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { updateLoyaltySettings } from '../../global/Firebase/firebase'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { constantsAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { buildLoyaltySettingsUpdate } from '../../utils/loyaltySettingsUpdate'

export default function LoyaltySettingsScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const constants = useAtomValue(constantsAtom)
  const [cashbackEnabled, setCashbackEnabled] = useState(constants?.loyaltySettings?.cashbackEnabled || false)
  const [defaultBonusLevel, setDefaultBonusLevel] = useState(constants?.loyaltySettings?.defaultBonusLevel?.toString())
  const [punchCardEnabled, setPunchCardEnabled] = useState(constants?.loyaltySettings?.punchCardEnabled || false)
  const [punchCardCapacity, setPunchCardCapacity] = useState(constants?.loyaltySettings?.punchCardCapacity?.toString())
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Emit detail data for E2E testing
  useEffect(() => {
    // ATL-953: report what the SAVE would write, not `parseInt(x) || 0` — the coercion this
    // screen's defect is about. `sanitizeForFirestore` would also render a NaN as `null`, so the
    // old line could not have shown the bug even while producing it.
    console.log('[LOYALTY_SETTINGS_DATA]', JSON.stringify(sanitizeForFirestore(
      buildLoyaltySettingsUpdate({
        cashbackEnabled,
        defaultBonusLevel,
        punchCardEnabled,
        punchCardCapacity,
      }).info ?? { cashbackEnabled, punchCardEnabled },
    )));
  }, []);

  async function save() {
    setIsLoading(true)
    // ATL-953: `parseInt()` returns NaN for a blank or junk field and Firestore STORES NaN, which
    // the backend then refuses to copy — see utils/loyaltySettingsUpdate.js for the whole defect.
    const built = buildLoyaltySettingsUpdate({
      cashbackEnabled,
      defaultBonusLevel,
      punchCardEnabled,
      punchCardCapacity,
    })
    if (built.refusal) {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
      return
    }
    setSaveError('')
    try {
      await updateLoyaltySettings(context.db, built.info)
      setSuccessMessageVisible(true)
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
      setSaveError(t('error'))
      setIsLoading(false)
    }
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        
        <Text category='h3' style={styles.header}>
        {t('loyaltySettings.cashbackName')}
        </Text>
        <Toggle
          testID="toggle-cashback"
          checked={cashbackEnabled}
          onChange={setCashbackEnabled}
          style={styles.toggleField}
        >
          {t('loyaltySettings.enabled')}
        </Toggle>
        <Text category='label' style={styles.label}>
          {t('loyaltySettings.defaultBonusLevel')}
        </Text>
        <StyledTextInput 
          value={defaultBonusLevel}
          onChangeText={text => setDefaultBonusLevel(text)}
          returnKeyType='done'
          keyboardType='number-pad'
        />

        <Text category='h3' style={styles.header}>
          {t('loyaltySettings.punchCardName')}
        </Text>
        <Toggle
          testID="toggle-punch-card"
          checked={punchCardEnabled}
          onChange={setPunchCardEnabled}
          style={styles.toggleField}
        >
          {t('loyaltySettings.enabled')}
        </Toggle>
        <Text category='label' style={styles.label}>
          {t('loyaltySettings.punchCardCapacity')}
        </Text>
        <StyledTextInput 
          value={punchCardCapacity}
          onChangeText={text => setPunchCardCapacity(text)}
          returnKeyType='done'
          keyboardType='number-pad'
        />
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}} />
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  header: {
    marginBottom: 10,
    marginTop: 20,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  toggleField: {
    alignSelf: 'flex-start'
  },
});